import React, { useRef } from 'react';
import {
  Controller,
  useForm,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import IntlTelInput from '@intl-tel-input/react';
import 'intl-tel-input/styles';
import { getRouteApi } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';

import {
  APIupdateProfile,
  loadUserProfile,
} from '../../api/Auth';

import Notices from '../Notices';

import { setNotice } from '../../helpers/notice';

import type { ProfileFormData } from '../../types/forms/profileform';

const Account: React.FC = () => {
  const routeApi = getRouteApi('/account');
  const { profile } = routeApi.useLoaderData();

  const phoneFieldRef = useRef<any>(null);

  const { mutate: updateProfile, isPending } = useMutation({
    mutationFn: async (requestData: ProfileFormData): Promise<ProfileFormData> => {
      const response = await APIupdateProfile(profile.id, requestData);
      return response.data;
    },
    onError: () => {
      setNotice('update.failed');
    },
    onSuccess: () => {
      setNotice('profile.updated');
      loadUserProfile();
    },
  });

  const schema = z.object({
    name: z.string().min(1, { message: "Name is required" }),
    phone: z.string().optional().refine((value) => {
      if (!value) return true;
      if (phoneFieldRef.current) {
        const iti = phoneFieldRef.current.getInstance();
        return iti.isValidNumber();
      }
    }, { message: "Invalid phone number format" }),
    phoneOptIn: z.boolean().optional(),
  });

  const {
    control,
    register,
    formState: {
      errors,
    },
    handleSubmit,
  } = useForm<ProfileFormData>({
    defaultValues: {
      name: profile.name || '',
      phone: profile.phone || '',
      phoneOptIn: profile.phone_opt_in || false,
    },
    resolver: zodResolver(schema) as any,
  });

  const submit = (data: ProfileFormData) => {
    setNotice(null);
    updateProfile(data);
  };

  return (
    <div className="container account-container account-profile-container">
      <div className="account-header page-header">
        <h2 className="page-title">My <em>profile</em></h2>
      </div>
      <div className="account-content account-profile-content">
        <Notices />
        <form onSubmit={handleSubmit(submit)} className="profile-form" noValidate>
          <wa-input
            label="Name"
            className={errors.name ? 'invalid' : ''}
            hint={errors.name ? errors.name.message : ''}
            required
            defaultValue={profile?.name}
            {...register("name")}
          />
          <label htmlFor="phone" className="label">Phone number</label>
          <Controller
            control={control}
            name="phone"
            render={({ field }) => (
              <div className={`intl-tel-input-container ${errors.phone ? 'invalid' : ''}`}>
                <IntlTelInput
                  countryOrder={["us","ca"]}
                  initialCountry="us"
                  loadUtils={() => import('intl-tel-input/utils')}
                  ref={phoneFieldRef}
                  inputProps={{ id: 'phone', defaultValue: profile?.phone }}
                  onChangeNumber={(number) => {
                    field.onChange(number);
                  }}
                />
                {errors.phone && <p className="error-message">{errors.phone.message}</p>}
              </div>
            )}
          />
          <div className="checkbox-container">
            <label htmlFor="phoneOptIn">
              <input type="checkbox" id="phoneOptIn" {...register('phoneOptIn')} /> I agree to receive SMS notifications
            </label>
          </div>
          <wa-button type="submit">
            {isPending ? 'Saving...' : 'Save'}
          </wa-button>
        </form>
      </div>
    </div>
  );
}

export default Account;
